/**
 * Backend API Service for Home Collections
 * Manages configurable product collections on the HOME view
 */

export interface CollectionItem {
  id: string;
  name: string;
  productIds: string[];
  isActive: boolean;
  displayOrder?: number;
  shuffleProducts?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface HomeCollectionsConfig {
  storeId: string;
  menuType: string;
  collections: CollectionItem[];
  lastEdited: string | null;
  lastAuthor: string;
  editors: string[];
}

export interface ScheduledCollectionData {
  documentId: string;
  name: string;
  productIds: string[];
  startDateTime?: Date;
  endDateTime?: Date;
  timezone?: string;
  status: 'scheduled' | 'current' | 'expired';
  menuType?: 'med' | 'rec';
  displayOrder?: number;
  createdAt?: string;
  updatedAt?: string;
  lastAuthor?: string;
  isRecurring?: boolean;
  recurringStartDay?: number;
  recurringStartTime?: string;
  recurringEndDay?: number;
  recurringEndTime?: string;
  shuffleProducts?: boolean;
}

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL!;

const getAuthToken = async (): Promise<string> => {
  // In Cypress test mode, return a dummy token (backend bypasses verification)
  if (process.env.REACT_APP_CYPRESS_TEST_MODE === 'true' &&
      window.location.hostname === 'localhost') {
    return 'cypress-test-token';
  }

  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();

  if (!auth.currentUser) {
    throw new Error('No authenticated user found');
  }

  return auth.currentUser.getIdToken();
};

/**
 * Get published collections for customer display (public, no auth)
 * Returns null if no collections are configured
 */
export const getPublishedCollections = async (
  storeName: string,
  menuType: string
): Promise<CollectionItem[] | null> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/home-collections/${storeName}/${menuType}`,
      { method: 'GET', headers: { 'Content-Type': 'application/json' } }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    if (result.success) {
      return result.data; // null or CollectionItem[]
    }

    return null;
  } catch (error) {
    console.error('Error fetching published collections:', error);
    return null; // Fail silently so frontend falls back to hardcoded
  }
};

/**
 * Get full collections config for admin panel (auth required)
 */
export const getCollectionsForAdmin = async (
  storeName: string,
  menuType: string
): Promise<HomeCollectionsConfig | null> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/api/home-collections/${storeName}/${menuType}/admin`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    return result.success ? result.data : null;
  } catch (error) {
    console.error('Error fetching collections for admin:', error);
    throw error;
  }
};

/**
 * Create a new collection
 */
export const createCollection = async (
  storeName: string,
  menuType: string,
  name: string,
  productIds: string[]
): Promise<string> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/api/home-collections/${storeName}/${menuType}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, productIds }),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to create collection');
    }

    return result.data.collectionId;
  } catch (error) {
    console.error('Error creating collection:', error);
    throw error;
  }
};

/**
 * Update an existing collection
 */
export const updateCollection = async (
  storeName: string,
  menuType: string,
  collectionId: string,
  updates: { name?: string; productIds?: string[]; isActive?: boolean; shuffleProducts?: boolean }
): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/api/home-collections/${storeName}/${menuType}/${collectionId}`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to update collection');
    }
  } catch (error) {
    console.error('Error updating collection:', error);
    throw error;
  }
};

/**
 * Delete a collection
 */
export const deleteCollection = async (
  storeName: string,
  menuType: string,
  collectionId: string
): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/api/home-collections/${storeName}/${menuType}/${collectionId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to delete collection');
    }
  } catch (error) {
    console.error('Error deleting collection:', error);
    throw error;
  }
};

/**
 * Reorder collections
 */
export const reorderCollections = async (
  storeName: string,
  menuType: string,
  orderedCollectionIds: string[]
): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/api/home-collections/${storeName}/${menuType}/reorder`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderedCollectionIds }),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to reorder collections');
    }
  } catch (error) {
    console.error('Error reordering collections:', error);
    throw error;
  }
};

// ==========================================
// Scheduled Collections
// ==========================================

/**
 * Get all scheduled collections for admin (current + scheduled + expired)
 */
export const getAllScheduledCollections = async (
  storeName: string,
  menuType: string
): Promise<ScheduledCollectionData[]> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/api/home-collections/${storeName}/${menuType}/all-scheduled`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    if (!result.success || !result.data) {
      return [];
    }

    // Convert date strings to Date objects
    return result.data.map((c: any) => ({
      ...c,
      startDateTime: c.startDateTime ? new Date(c.startDateTime) : undefined,
      endDateTime: c.endDateTime ? new Date(c.endDateTime) : undefined,
    }));
  } catch (error) {
    console.error('Error fetching scheduled collections:', error);
    throw error;
  }
};

/**
 * Create a new scheduled collection
 */
export const createScheduledCollection = async (
  storeName: string,
  menuType: string,
  data: {
    name: string;
    productIds: string[];
    startDateTime?: Date;
    endDateTime?: Date;
    displayOrder?: number;
    isRecurring?: boolean;
    recurringStartDay?: number;
    recurringStartTime?: string;
    recurringEndDay?: number;
    recurringEndTime?: string;
    shuffleProducts?: boolean;
  }
): Promise<{ documentId: string }> => {
  try {
    const token = await getAuthToken();
    const body: Record<string, any> = {
      name: data.name,
      productIds: data.productIds,
      displayOrder: data.displayOrder,
    };
    if (data.shuffleProducts !== undefined) body.shuffleProducts = data.shuffleProducts;
    if (data.isRecurring) {
      body.isRecurring = true;
      body.recurringStartDay = data.recurringStartDay;
      body.recurringStartTime = data.recurringStartTime;
      body.recurringEndDay = data.recurringEndDay;
      body.recurringEndTime = data.recurringEndTime;
    } else {
      body.startDateTime = data.startDateTime!.toISOString();
      body.endDateTime = data.endDateTime!.toISOString();
    }
    const response = await fetch(
      `${API_BASE_URL}/api/home-collections/${storeName}/${menuType}/scheduled`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to create scheduled collection');
    }

    return result.data;
  } catch (error) {
    console.error('Error creating scheduled collection:', error);
    throw error;
  }
};

/**
 * Update a scheduled collection
 */
export const updateScheduledCollection = async (
  storeName: string,
  menuType: string,
  collectionId: string,
  updates: {
    name?: string;
    productIds?: string[];
    startDateTime?: Date;
    endDateTime?: Date;
    displayOrder?: number;
    isRecurring?: boolean;
    recurringStartDay?: number;
    recurringStartTime?: string;
    recurringEndDay?: number;
    recurringEndTime?: string;
    shuffleProducts?: boolean;
  }
): Promise<void> => {
  try {
    const token = await getAuthToken();
    const body: Record<string, any> = {};
    if (updates.name !== undefined) body.name = updates.name;
    if (updates.productIds !== undefined) body.productIds = updates.productIds;
    if (updates.startDateTime !== undefined) body.startDateTime = updates.startDateTime.toISOString();
    if (updates.endDateTime !== undefined) body.endDateTime = updates.endDateTime.toISOString();
    if (updates.displayOrder !== undefined) body.displayOrder = updates.displayOrder;
    if (updates.isRecurring !== undefined) body.isRecurring = updates.isRecurring;
    if (updates.recurringStartDay !== undefined) body.recurringStartDay = updates.recurringStartDay;
    if (updates.recurringStartTime !== undefined) body.recurringStartTime = updates.recurringStartTime;
    if (updates.recurringEndDay !== undefined) body.recurringEndDay = updates.recurringEndDay;
    if (updates.recurringEndTime !== undefined) body.recurringEndTime = updates.recurringEndTime;
    if (updates.shuffleProducts !== undefined) body.shuffleProducts = updates.shuffleProducts;

    const response = await fetch(
      `${API_BASE_URL}/api/home-collections/${storeName}/${menuType}/scheduled/${collectionId}`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to update scheduled collection');
    }
  } catch (error) {
    console.error('Error updating scheduled collection:', error);
    throw error;
  }
};

/**
 * Delete a scheduled collection
 */
export const deleteScheduledCollection = async (
  storeName: string,
  menuType: string,
  collectionId: string
): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/api/home-collections/${storeName}/${menuType}/scheduled/${collectionId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to delete scheduled collection');
    }
  } catch (error) {
    console.error('Error deleting scheduled collection:', error);
    throw error;
  }
};

/**
 * Reorder scheduled collections
 */
export const reorderScheduledCollections = async (
  storeName: string,
  menuType: string,
  orderData: Array<{ documentId: string; displayOrder: number }>
): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/api/home-collections/${storeName}/${menuType}/scheduled/reorder`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderData }),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to reorder scheduled collections');
    }
  } catch (error) {
    console.error('Error reordering scheduled collections:', error);
    throw error;
  }
};

export const reorderUnified = async (
  storeName: string,
  menuType: string,
  orderData: Array<{ id: string; type: 'always-on' | 'scheduled'; displayOrder: number }>
): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/api/home-collections/${storeName}/${menuType}/reorder-unified`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderData }),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to reorder collections');
    }
  } catch (error) {
    console.error('Error reordering unified collections:', error);
    throw error;
  }
};
